import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SessionService } from './session.service';

/**
 * Blocking modal shown briefly while SessionService auto-redirects the
 * user to Auth0's login screen. Previously this dialog required an
 * explicit "Sign in again" button click — users expected the UI to
 * handle the redirect for them, and the extra click surfaced as "the
 * site is broken, nothing happens when it expires". markExpired()
 * now kicks off the redirect automatically; this dialog's only job
 * is to tell the user what's happening during the ~1 round-trip to
 * Auth0's /authorize endpoint before the SPA unloads.
 *
 * closable=false, dismissableMask=false, [closeOnEscape]=false — the
 * user can't accidentally dismiss the modal and be left staring at a
 * broken app while the redirect is in flight.
 */
@Component({
  selector: 'app-session-expired-dialog',
  standalone: true,
  imports: [DialogModule, ProgressSpinnerModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [visible]="isExpired()"
      [modal]="true"
      [closable]="false"
      [dismissableMask]="false"
      [closeOnEscape]="false"
      [draggable]="false"
      [resizable]="false"
      header="Signing you back in"
      [style]="{ width: '26rem' }"
    >
      <div class="body">
        <p-progressSpinner strokeWidth="3" styleClass="spinner" />
        <p class="message">
          Your session has expired. Redirecting you to the sign-in screen&hellip;
        </p>
      </div>
    </p-dialog>
  `,
  styles: [
    `
      .body {
        display: flex;
        align-items: center;
        gap: 1rem;
      }
      :host ::ng-deep .spinner {
        width: 2.25rem;
        height: 2.25rem;
        flex: 0 0 auto;
      }
      .message {
        margin: 0;
        color: var(--color-text-secondary);
        line-height: 1.5;
      }
    `,
  ],
})
export class SessionExpiredDialogComponent {
  // Narrow surface: the template only needs the visibility signal; the
  // redirect is driven by SessionService.markExpired itself now, so the
  // dialog is purely presentational.
  private readonly session = inject(SessionService);
  readonly isExpired = this.session.isExpired;
}
